import type { FetchResponse } from 'openapi-fetch';
import type { SuccessResponse } from 'openapi-typescript-helpers';
import type { paths } from './coreApiSchema';
import { getItem } from '../utils/storage';
import { coreApiClient } from './apiClient';
import {
  functionToMenuItemMapper,
  getLegacyGroupById,
  getLegacyGroupMembers,
  getLegacyGroups,
  typeToGroupTypeMapper,
} from './legacyGroups';
import type { GroupMemberViewModel, GroupViewModelExtended } from '../apis/groupsAPI/types';
import { convertIsoDateToLegacy, legacyImageUrlsMapper } from './legacyCommonMappers';
import type { GroupMember } from '../containers/GroupDetail/groupDetailSlice/types';
import { shouldUseCoreApi } from '../utils/features';
import { API_VERSION } from '../config/app';

// Type that allows for extending openapi-fetch response data.
// If a prop in CustomData is already defined in Paths, the CustomData prop will replace
// the prop in Paths.
type ExtendedResponseData<
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Paths extends Record<string | number, any>,
  StatusCode extends number,
  CustomData extends Record<string, unknown>,
> = Omit<Paths, 'responses'> & {
  responses: Omit<Paths['responses'], StatusCode> &
    Record<
      StatusCode,
      Omit<Paths['responses'][StatusCode], 'content'> & {
        content: Omit<Paths['responses'][StatusCode]['content'], 'application/json'> & {
          'application/json': Omit<
            Paths['responses'][StatusCode]['content']['application/json'],
            'data'
          > & {
            data: Paths['responses'][StatusCode]['content']['application/json']['data'] extends unknown[]
              ? (Omit<
                  Paths['responses'][StatusCode]['content']['application/json']['data'][number],
                  keyof CustomData
                > &
                  CustomData)[]
              : Omit<
                  Paths['responses'][StatusCode]['content']['application/json']['data'],
                  keyof CustomData
                > &
                  CustomData;
          };
        };
      }
    >;
};

type ExtendedGroupData = ExtendedResponseData<
  paths['/core/v1/groups/{groupId}']['get'],
  200,
  Pick<
    GroupViewModelExtended,
    | 'groupType'
    | 'imageFileName'
    | 'groupMembersCount'
    | 'member'
    | 'organizationID'
    | 'organizationName'
    | 'subOrganizationID'
    | 'subOrganizationname'
    | 'menuItems'
  >
>;

type GetGroupResponse = FetchResponse<ExtendedGroupData, { parseAs: 'json' }, 'application/json'>;
export type GroupData = SuccessResponse<ExtendedGroupData['responses']>['data'];

export const getGroupById = async ({
  groupId,
  tenantId,
}: paths['/core/v1/groups/{groupId}']['get']['parameters']['path'] & {
  tenantId: number;
}): Promise<GetGroupResponse> => {
  if (!shouldUseCoreApi()) {
    return getLegacyGroupById({ groupId });
  }

  const res = await coreApiClient.GET('/core/v1/groups/{groupId}', {
    params: {
      path: { groupId },
      header: {
        authorization: `Bearer ${getItem('coreApiAccessToken')}`,
        'cosafe-api-version': String(API_VERSION),
        'cosafe-tenant-id': String(tenantId),
      },
    },
  });

  if (res.error) return Promise.resolve(res);

  return Promise.resolve({
    ...res,
    data: {
      ...res.data,
      data: {
        ...res.data.data,
        // TODO: Should remove "groupType" in favor of only using "type"
        groupType: typeToGroupTypeMapper[res.data.data.type],
        // TODO: Should use "imageUrls" instead of "imageFileName"
        imageFileName: res.data.data.imageUrls ? res.data.data.imageUrls.small : null,
        // TODO: Handle when consuming GET groups from core-api
        groupMembersCount: res.data.data.membersCount,
        member: null,
        organizationID:
          res.data.data.account.id === res.data.data.account.tenantId
            ? res.data.data.account.id
            : res.data.data.account.tenantId,
        organizationName:
          res.data.data.account.id === res.data.data.account.tenantId
            ? res.data.data.account.name
            : null,
        subOrganizationID:
          res.data.data.account.tenantId === res.data.data.account.id
            ? null
            : res.data.data.account.id,
        subOrganizationname:
          res.data.data.account.tenantId === res.data.data.account.id
            ? null
            : res.data.data.account.name,
        menuItems: null,
      },
    },
  });
};

type ExtendedGroupsData = ExtendedResponseData<
  paths['/core/v1/groups']['get'],
  200,
  Pick<
    GroupViewModelExtended,
    | 'groupType'
    | 'type'
    | 'imageFileName'
    | 'groupMembersCount'
    | 'member'
    | 'organizationID'
    | 'organizationName'
    | 'subOrganizationID'
    | 'subOrganizationname'
    | 'menuItems'
  >
>;

type GetGroupsResponse = FetchResponse<ExtendedGroupsData, { parseAs: 'json' }, 'application/json'>;
export type GroupsData = SuccessResponse<ExtendedGroupsData['responses']>['data'][number];
export type GetGroupsQuery = NonNullable<paths['/core/v1/groups']['get']['parameters']['query']>;

export const getGroups = async (
  tenantId: number,
  query: GetGroupsQuery = {}
): Promise<GetGroupsResponse> => {
  const member = !!query?.memberUserId;

  if (!shouldUseCoreApi()) {
    return getLegacyGroups({
      limit: query?.limit,
      skip: query?.offset || undefined,
      minGroupMembersCount: query?.['membersCount[gte]'],
      member, // Current implementation only supports passing requesting user id
      search: query?.['name[match]'],
      ...(query?.['function[in]'] && { menuitem: functionToMenuItemMapper[query['function[in]']] }),
    });
  }

  const result = await coreApiClient.GET('/core/v1/groups', {
    params: {
      query,
      header: {
        authorization: `Bearer ${getItem('coreApiAccessToken')}`,
        'cosafe-api-version': String(API_VERSION),
        'cosafe-tenant-id': String(tenantId),
      },
    },
  });

  if (result.error) return Promise.resolve(result);

  return Promise.resolve({
    ...result,
    data: {
      ...result.data,
      data: result.data.data.map(group => ({
        ...group,
        type: typeToGroupTypeMapper[group.type], // Mapping to legacy type to make frontend migration easier
        // TODO: Should remove "groupType" in favor of only using "type"
        groupType: typeToGroupTypeMapper[group.type],
        // TODO: Should use "imageUrls" instead of "imageFileName"
        imageFileName: legacyImageUrlsMapper.fromCoreToLegacy(group.imageUrls),
        // TODO: Handle when consuming GET groups from core-api
        groupMembersCount: group.membersCount,
        member,
        organizationID:
          group.account.id === group.account.tenantId ? group.account.id : group.account.tenantId,
        organizationName: group.account.id === group.account.tenantId ? group.account.name : null,
        subOrganizationID: group.account.tenantId === group.account.id ? null : group.account.id,
        subOrganizationname:
          group.account.tenantId === group.account.id ? null : group.account.name,
        menuItems: null,
      })),
    },
  });
};

type GetGroupMembersPathParams =
  paths['/core/v1/groups/{groupId}/members']['get']['parameters']['path'];
type ExtendedGroupMembersData = ExtendedResponseData<
  paths['/core/v1/groups/{groupId}/members']['get'],
  200,
  Pick<
    GroupMemberViewModel,
    | 'groupID'
    | 'photoFileName'
    | 'userID'
    | 'userName'
    | 'info'
    | 'unavailableDateTo'
    | 'userEmail'
    | 'admin'
    | 'lastAccess'
  >
>;
type GetGroupMembersResponse = FetchResponse<
  ExtendedGroupMembersData,
  { parseAs: 'json' },
  'application/json'
>;
export type GetGroupMembersQuery =
  paths['/core/v1/groups/{groupId}/members']['get']['parameters']['query'];
export type GroupMemberData = SuccessResponse<
  ExtendedGroupMembersData['responses']
>['data'][number];

export const getGroupMembers = async (
  params: GetGroupMembersPathParams,
  tenantId: number,
  query: GetGroupMembersQuery = {}
): Promise<GetGroupMembersResponse> => {
  if (!shouldUseCoreApi()) {
    return getLegacyGroupMembers({
      groupId: params.groupId,
    });
  }

  const res = await coreApiClient.GET('/core/v1/groups/{groupId}/members', {
    params: {
      path: { groupId: params.groupId },
      query,
      header: {
        authorization: `Bearer ${getItem('coreApiAccessToken')}`,
        'cosafe-api-version': String(API_VERSION),
        'cosafe-tenant-id': String(tenantId),
      },
    },
  });

  if (res.error) return Promise.resolve(res);

  return Promise.resolve({
    ...res,
    data: {
      ...res.data,
      data: [
        ...res.data.data.map(member => ({
          ...member,
          groupID: params.groupId,
          lastAccessAt: member.lastAccessAt ? convertIsoDateToLegacy(member.lastAccessAt) : null,
          unavailableUntil: member.unavailableUntil
            ? convertIsoDateToLegacy(member.unavailableUntil)
            : null,
          userID: member.id,
          info: member.title,
          unavailableDateTo: member.unavailableUntil
            ? convertIsoDateToLegacy(member.unavailableUntil)
            : null,
          userEmail: member.email,
          userName: member.name,
          admin: member.isAdmin,
          lastAccess: member.lastAccessAt ? convertIsoDateToLegacy(member.lastAccessAt) : null,
          photoFileName: member?.imageUrls?.small.split('/').pop()?.replace('_small', '') ?? null,
        })),
      ],
    },
  });
};

export function isGroupSuccessData(arg: GetGroupResponse): arg is Omit<
  GetGroupResponse,
  'data' | 'error'
> & {
  error?: undefined;
  data: NonNullable<GetGroupResponse['data']>;
} {
  return !arg.error;
}

export async function fetchAllGroupMembers({
  groupId,
  tenantId,
}: paths['/core/v1/groups/{groupId}/members']['get']['parameters']['path'] & {
  tenantId: number;
}): Promise<GroupMember[]> {
  const groupMembers: GroupMember[] = [];
  let offset = 0;
  let iteration = 0;
  const limit = 100;

  while (true) {
    if (iteration >= 10) {
      throw new Error('Failed to fetch group members, to many iterations');
    }
    const response = await getGroupMembers({ groupId: groupId }, tenantId, {
      offset,
      limit,
    });
    if (response.error) {
      throw new Error('Failed to fetch group members');
    }

    groupMembers.push(...response.data.data);

    if (groupMembers.length >= response.data.metadata.pagination.totalCount) {
      break;
    }

    offset += limit;
    iteration++;
  }

  return groupMembers;
}
